import type { ReactElement } from 'react';
import Box from '@mui/material/Box';
import type { ReachState } from '../../core/reach/reach.store';

/**
 * 036 (issue 208, FR-005, contracts/what-the-practitioner-sees.md "The connection line") — the one
 * statement, on every route, that the server cannot presently be asked. Distinct from 021's
 * `OfflinePanel`, which replaces a whole screen's content: this is a single line that never hides
 * anything, because `layout.tsx` and `app-root.tsx` both reserve exactly its height for it rather
 * than overlaying it on top of the content beneath (SC-022's "covers no content at any width").
 *
 * **A single line, never two**, which is why the height is fixed rather than left to wrap: at any
 * width narrower than the longer of the two sentences, the text truncates with an ellipsis instead
 * of wrapping onto a second line. A wrapping line would grow the space the two shells reserve for
 * it beyond what either can predict without measuring the DOM, which is exactly the complexity a
 * fixed, single-line height avoids.
 *
 * `role="status"`, `aria-live="polite"` — announced without moving focus and without interrupting
 * whatever a screen reader is already reading, the same reasoning `OfflinePanel` states for itself.
 * `alert`/`assertive` would be wrong for the same reason: the condition arrives without the
 * practitioner doing anything.
 */
export const CONNECTION_LINE_HEIGHT_PX = 32;

/** `reachable` never reaches here — both shells render nothing at all in that state (the contract's
 *  own "nothing is shown" row) — so this only ever has to spell the other two. */
const WORDS: Readonly<Record<Exclude<ReachState, 'reachable'>, string>> = {
  unknown: '', // never shown (see below) — present only so the type is total
  unreachable: 'No connection.',
  failing: "CompleteMe's server is not answering.",
};

export function ConnectionLine({ state }: { readonly state: ReachState }): ReactElement | null {
  // 'unknown' is what every session starts in, before a single request has settled either way — it
  // is not a claim the server cannot be reached, and reading it as one would show this line on
  // every cold load for the instant before the first request answers (the absence of a distinction
  // read as one of its branches, which `reach.store.ts`'s own header already warns against).
  if (state === 'reachable' || state === 'unknown') return null;

  return (
    <Box
      role="status"
      aria-live="polite"
      data-testid="connection-line"
      sx={{
        height: `${CONNECTION_LINE_HEIGHT_PX}px`,
        lineHeight: `${CONNECTION_LINE_HEIGHT_PX}px`,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        padding: '0 var(--space-3)',
        background: 'var(--rule)',
        color: 'var(--ink)',
        fontSize: 'var(--text-sm)',
        textAlign: 'center',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none', animation: 'none' },
      }}
    >
      {WORDS[state]}
    </Box>
  );
}
